import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import { type ClubTermRow, clubPlanTerms } from '../../domain/club';
import type { MembershipFormatters } from '../../domain/format';
import type { MembershipPlan } from '../../domain/types';
import { MEMBERSHIP_TEXT } from '../text';
import { CLUB_CLASS } from './clubStyles';

const TermsGroup = ({
  title,
  rows,
}: {
  title: string;
  rows: ClubTermRow[];
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col">
      <h3
        className={cn(
          'm-0 mb-1 font-medium desktop:text-[14px] text-[13px] leading-4',
          CLUB_CLASS.muted,
        )}
      >
        {title}
      </h3>
      <ul className="m-0 flex list-none flex-col p-0">
        {rows.map((row) => (
          <li
            key={row.id}
            data-testid={`club-plan-terms-row-${row.id}`}
            className="flex items-center justify-between gap-4 border-[#e4e3e3] border-b py-3.5 desktop:text-[16px] text-[15px] leading-[18px] last:border-b-0"
          >
            <span>{t(row.label.key, row.label.params)}</span>
            <span className="shrink-0 font-semibold">
              {t(row.value.key, row.value.params)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * "Plan terms" card of the club page: free delivery and every discount step
 * of `plan`, then whether reduced items are covered; nothing for a plan with
 * neither.
 */
export const ClubPlanTerms = ({
  plan,
  formatters,
}: {
  plan: MembershipPlan;
  formatters: MembershipFormatters;
}) => {
  const { t } = useTranslation();
  const terms = clubPlanTerms(plan, formatters);

  if (terms.delivery.length === 0 && terms.discount.length === 0) {
    return null;
  }

  return (
    <section
      data-testid="club-plan-terms"
      className={cn(
        'flex flex-col desktop:gap-5 gap-4 desktop:px-8 px-5 desktop:py-7 py-6',
        CLUB_CLASS.card,
      )}
    >
      <h2 className="m-0 font-semibold desktop:text-[20px] text-[18px] desktop:leading-[21px] leading-5 tracking-[-0.01em]">
        {t(MEMBERSHIP_TEXT.clubTermsTitle)}
      </h2>
      {terms.delivery.length > 0 && (
        <TermsGroup
          title={t(MEMBERSHIP_TEXT.clubTermsDelivery)}
          rows={terms.delivery}
        />
      )}
      {terms.discount.length > 0 && (
        <TermsGroup
          title={t(MEMBERSHIP_TEXT.clubTermsDiscount)}
          rows={terms.discount}
        />
      )}
      {terms.markdown && (
        <p
          className={cn(
            'm-0 desktop:text-[14px] text-[13px] leading-5',
            CLUB_CLASS.muted,
          )}
        >
          {t(terms.markdown.key, terms.markdown.params)}
        </p>
      )}
    </section>
  );
};
